import { FormEvent, useState } from 'react';
import { authApi } from '../api';

interface ChangePasswordDialogProps {
  username: string;
  onClose: () => void;
  onSuccess: () => void;
}

const MIN_PASSWORD_LEN = 12;

/**
 * Modal that lets the currently-logged-in administrator change their
 * own password.  Hard rules:
 *
 *   * Requires the current password (verified server-side via scrypt).
 *   * New password must be at least 12 chars and must be confirmed
 *     twice (client-side and server-side via Pydantic min_length).
 *   * Never reads / writes the internal user id or password hash.
 *   * Shows a generic error on failure; never reveals whether the
 *     username exists or the hash scheme.
 */
export function ChangePasswordDialog({
  username,
  onClose,
  onSuccess,
}: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (newPassword.length < MIN_PASSWORD_LEN) {
      setError(`新密码至少需要 ${MIN_PASSWORD_LEN} 个字符`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      return;
    }
    if (newPassword === currentPassword) {
      setError('新密码不能与当前密码相同');
      return;
    }
    setBusy(true);
    try {
      await authApi.changePassword(currentPassword, newPassword, confirmPassword);
      setSuccess('密码已更新，请使用新密码重新登录');
      // Server rotates the session cookie in the same response. We
      // close the dialog and bounce the user back to /login so they
      // can verify the new credential end-to-end.
      setTimeout(() => {
        onSuccess();
        window.location.assign('/login');
      }, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : '修改失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={() => !busy && onClose()}
    >
      <section
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2 id="change-password-title">修改登录密码</h2>
          <button
            className="ghost-button"
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="关闭"
          >
            关闭
          </button>
        </header>
        <p className="muted-text" data-testid="change-password-username">
          当前登录账号：<strong>{username || '未识别'}</strong>
        </p>
        <form onSubmit={submit} className="key-form">
          <label>
            <span>当前密码</span>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              spellCheck={false}
            />
          </label>
          <label>
            <span>新密码（至少 {MIN_PASSWORD_LEN} 个字符）</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={MIN_PASSWORD_LEN}
              required
              spellCheck={false}
            />
          </label>
          <label>
            <span>再次输入新密码</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={MIN_PASSWORD_LEN}
              required
              spellCheck={false}
            />
          </label>
          {error ? <div className="soft-notice danger" role="alert"><strong>修改失败</strong><span>{error}</span></div> : null}
          {success ? <div className="success-banner" role="status">{success}</div> : null}
          <div className="modal-actions">
            <button className="ghost-button" type="button" onClick={() => !busy && onClose()} disabled={busy}>
              取消
            </button>
            <button
              className="primary-button"
              type="submit"
              disabled={busy || !currentPassword || !newPassword || !confirmPassword}
              data-testid="change-password-submit"
            >
              {busy ? '保存中...' : '保存新密码'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
